import { useMemo, useState } from 'react';
import { Building2, Inbox, Mail, Phone, RefreshCw, Trash2 } from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Loading,
  Segmented,
  Stat,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { phone, phoneDial, relative, titleCase } from '../lib/format.js';

const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

export default function Leads() {
  const toast = useToast();
  const [status, setStatus] = useState('all');
  const { data, meta, loading, refresh } = useApi('/public/leads', { params: { status } });
  const leads = data || [];

  const setLeadStatus = async (lead, next) => {
    try {
      await api.patch(`/public/leads/${lead.id}`, { status: next });
      toast.ok(`${lead.company || lead.name} → ${next}`);
      refresh();
    } catch (err) {
      toast.err('Could not update the lead', err.message);
    }
  };

  return (
    <>
      <PageHead
        title="Leads"
        subtitle="Facilities and brokers that asked us for coverage from the website."
        icon={Inbox}
        actions={<Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>}
      />

      <div className="grid grid-4 mb-20">
        <Stat label="New" icon={Inbox} value={meta?.new || 0} tone="amber" foot="waiting on a first call" />
        <Stat label="Contacted" icon={Mail} value={meta?.contacted || 0} tone="blue" foot="in conversation" />
        <Stat label="Quoted" icon={Building2} value={meta?.quoted || 0} tone="violet" foot="pricing sent" />
        <Stat label="Won" icon={Building2} value={meta?.won || 0} tone="green" foot="converted to accounts" />
      </div>

      <div className="filter-bar">
        <Segmented options={STATUSES} value={status} onChange={setStatus} />
        <div className="grow" />
        <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{leads.length} leads</span>
      </div>

      {loading && !leads.length ? (
        <Loading rows={6} />
      ) : leads.length ? (
        <div className="grid grid-auto-lg stagger">
          {leads.map((lead) => (
            <Card key={lead.id}>
              <CardBody>
                <div className="row between gap-12">
                  <div className="grow truncate">
                    <div style={{ fontWeight: 640 }}>{lead.company || lead.name}</div>
                    <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                      {lead.name}{lead.city ? ` · ${lead.city}, ${lead.state}` : ''}
                    </div>
                  </div>
                  <Badge tone={lead.status === 'won' ? 'green' : lead.status === 'new' ? 'amber' : 'blue'} size="sm">
                    {titleCase(lead.status)}
                  </Badge>
                </div>

                {lead.volume ? (
                  <div className="row gap-8 mt-12">
                    <Badge tone="muted" size="sm">{lead.volume}</Badge>
                    <Badge tone="muted" size="sm">{titleCase(lead.kind)}</Badge>
                  </div>
                ) : null}

                {lead.message ? (
                  <p className="muted mt-12" style={{ fontSize: 'var(--fs-sm)' }}>{lead.message}</p>
                ) : null}

                <div className="divider" />
                <div className="row gap-12 wrap" style={{ fontSize: 'var(--fs-sm)' }}>
                  <a href={`mailto:${lead.email}`} className="row gap-6 dim"><Mail size={12} /> {lead.email}</a>
                  {lead.phone ? <a href={`tel:${phoneDial(lead.phone)}`} className="row gap-6 dim"><Phone size={12} /> {phone(lead.phone)}</a> : null}
                </div>
              </CardBody>
              <div className="card-foot">
                <div className="row between gap-8">
                  <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{relative(lead.createdAt)}</span>
                  <div className="row gap-6">
                    {STATUSES.slice(1, 5).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={cx('saved-view', lead.status === option.value && 'active')}
                        onClick={() => setLeadStatus(lead, option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState icon={Inbox} title="No leads in this view">
            Website enquiries land here the moment a facility asks for coverage.
          </EmptyState>
        </Card>
      )}
    </>
  );
}
